import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Link, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

type HelpDialogProps = DialogProps & {
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  onClose: () => void;
};

const HelpDialog = ({ fullWidth = true, maxWidth = 'lg', ...props }: HelpDialogProps) => {
  const { onClose } = props;
  return (
    <Dialog
      slotProps={{ paper: { sx: { userSelect: 'text' } } }}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      {...props}
    >
      <DialogTitle>About List Manager</DialogTitle>
      <DialogContent style={{ userSelect: 'text' }}>
        <p>
          <strong>List Manager</strong> is a tool for Australian Greens campaigners to recruit and
          coordinate the work of volunteers. It has a wide variety of features, organised by
          releases:
        </p>

        {
          <Accordion defaultExpanded elevation={3}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="release4-content"
              id="release4-header"
            >
              <Typography component="span">Release Four</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ul>
                <li>
                  Search Templates with placeholder fields for local teams to make use of a curated
                  library of searches
                </li>
              </ul>
            </AccordionDetails>
          </Accordion>
        }
        <Accordion elevation={3}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="release3-content"
            id="release3-header"
          >
            <Typography component="span">Release Three</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ul>
              <li>
                Advanced Search for more complex queries using AND/OR as well as NOT operators
              </li>
              <li>Associate lists with campaigns when creating them</li>
              <li>
                New Calling Status search filter to include or exclude people in searches already
                contacted
              </li>
              <li>
                Principle coordinator role for state/territory level list management and oversight
              </li>
              <li>
                CiviCRM comms preferences filter to include or exclude people who have opted out of
                calls, texts and so on
              </li>
              <li>
                Manual geographic placement option for the location filter and a new Statistical
                Area 1 filter for improved geographic filtering
              </li>
              <li>
                Expanded list of activities to search by in the Engagement History filter, including
                manual entry via gVIRS or Rocket, and yard sign registration
              </li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <Accordion elevation={3}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="release2-content"
            id="release2-header"
          >
            <Typography component="span">Release Two</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ul>
              <li>Calling statuses for better analysis and tracking of work</li>
              <li>Add/remove individual contacts, not just whole lists of contacts</li>
              <li>Release/reassign contacts in lists</li>
              <li>Reserve contacts in a list for future assignment</li>
              <li>Volunteer pathway support</li>
              <li>Additional list/search actions</li>
              <li>PDF export to support sign installation, collection, etc.</li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <Accordion elevation={3}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="release2-content"
            id="release2-header"
          >
            <Typography component="span">Release One</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ul>
              <li>Quick search for building simple lists</li>
              <li>Save searches for reuse, for yourself, and others</li>
              <li>Coordinator and Caller roles for managing access</li>
              <li>List assignments to delegate calling work</li>
              <li>List/search actions to perform certain tasks in bulk (eg. add/remove tags)</li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <p>
          <Link
            href="https://training.greens.org.au/courses/list-manager/"
            target="_blank"
            rel="noopener"
          >
            View all the List Manager help guides on our Training Hub.
          </Link>
        </p>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
          }}
          color="primary"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpDialog;
